import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { filterByLabel } from "@/lib/filterByLabel";
import DropdownService from "@/services/APIs/DropdownService";
import UserPermissionsService from "@/services/APIs/UserPermissionsService";
import { get } from "@/services/axiosUitils";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useAdminNavigate from "@/hooks/useAdminNavigate";

const UserPermissionsPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useAdminNavigate();
  const [searchParams] = useSearchParams();
  const user = searchParams.get("user");
  const schema = searchParams.get("schema");

  const [selectedUser, setSelectedUser] = useState<string | null>(user ?? null);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(
    schema ?? null
  );

  const [forValue, setForValue] = useState<string[]>([]);

  const { data: schemaOptions, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  //   const {
  //     data,
  //     isLoading,
  //     refetch: refetchUsers,
  //   } = useQuery({
  //     queryKey: ["tenantUsers", tenantId, page, pageSize],
  //     queryFn: () => TenantUserService.listTenantUsers(page, pageSize),
  //     enabled: !!tenantId,
  //     refetchOnWindowFocus: false,
  //     refetchOnReconnect: false,
  //     staleTime: 60_000,
  //   });

  const { data: userOptions, isLoading: userLoading } = useQuery({
    queryKey: ["user"],
    queryFn: () => DropdownService.getDropdownData("user"),
  });

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use React Query for mutations
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);
    const payload = {
      user: values.user_label,
      allow_schema: values.allow_schema_name,
      for_value: values.for_value_label,
    };
    if (isEditMode) {
      // Check forde changes before updating
      const transformedPayload = {
        user_label: values.user_label,
        allow_schema_name: values.allow_schema_name,
        for_value_label: values.for_value_label,
      };
      if (
        !checkFormChanges(transformedPayload, userPermissions ?? {}, notify)
      ) {
        setIsSubmitting(false);
        return;
      }

      const response = await UserPermissionsService.updateUserPermissions(
        id ?? "",
        payload
      );
      if (response) {
        navigate(navigationRoutes.USER_PERMISSIONS);
      }
    } else {
      const response =
        await UserPermissionsService.createUserPermissions(payload);
      if (response) {
        navigate(navigationRoutes.USER_PERMISSIONS);
      }
    }
    setIsSubmitting(false);
  };

  // Use React Query for data fetching
  const { data: userPermissions, isLoading } = useQuery({
    queryKey: ["userPermissions", id],
    queryFn: () => UserPermissionsService.getUserPermissionsById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && userPermissions) {
      const fetchSchemaLabel = async () => {
        const schema = userPermissions.allow_schema_name;
        const response = await get(`/dropdown/${schema}/`);
        setForValue(response ?? []);
      };
      fetchSchemaLabel();
      form.setFieldsValue(userPermissions);
    }
  }, [isEditMode, userPermissions, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const handleSchemaChange = async (value: string, option: any) => {
    setSelectedSchema(value);
    // Update selected schema
    try {
      const response = await get(`/dropdown/${option.label}/`);
      console.log(response); // Log the response from the API
      setForValue(response ?? []);
    } catch (error) {
      console.error("Error fetching data for schema:", error); // Handle any error
      setForValue([]);
    }
  };

  const handleForValueChange = (value: string) => {
    console.log(value);
  };

  if (isEditMode && !userPermissions) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit User Permissions" : "Add User Permissions"}
          onBack={() => navigate(navigationRoutes.USER_PERMISSIONS)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode
                  ? "Save User Permissions Changes"
                  : "Save New User Permissions"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="max-w-xl"
      >
        {/* New Fields */}
        <Form.Item
          name="user_label"
          label="User"
          rules={[{ required: true, message: "Please enter user" }]}
        >
          <Select
            options={userOptions?.map((user: any) => ({
              value: user.value,
              label: user.label,
            }))}
            placeholder="Select User"
            onChange={value => {
              setSelectedUser(value);
            }}
            value={selectedUser}
            allowClear
            loading={userLoading}
            filterOption={filterByLabel}
          />
        </Form.Item>

        <Form.Item
          name="allow_schema_name"
          label="Allow Schema"
          rules={[{ required: true, message: "Please enter allow schema" }]}
        >
          <Select
            options={schemaOptions?.map((schema: any) => ({
              value: schema.value,
              label: schema.label,
            }))}
            placeholder="Select Schema"
            onChange={handleSchemaChange}
            value={selectedSchema}
            allowClear
            loading={schemaLoading}
            filterOption={filterByLabel}
          />
        </Form.Item>

        <Form.Item
          name="for_value_label"
          label="For Value"
          rules={[{ required: true, message: "Please enter for value" }]}
        >
          <Select
            options={forValue?.map((item: any) => ({
              value: item.value,
              label: item.label,
            }))}
            placeholder="Select For Value"
            onChange={handleForValueChange}
            allowClear
            loading={false}
            filterOption={filterByLabel}
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UserPermissionsPage;
